import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry, when configured, captures uncaught exceptions automatically;
    // logging here keeps the message visible in dev too.
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-surface">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-semibold text-copy">Something went wrong.</h1>
          <p className="text-sm text-copy-muted">
            The page hit an error and couldn't render. The team has been notified — try refreshing,
            or head back to the home page.
          </p>
          <div className="flex justify-center gap-2 pt-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
            >
              Try again
            </button>
            <a
              href="/"
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-copy hover:bg-surface-sunken"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
